import get from 'lodash-es/get'
import last from 'lodash-es/last'

import {
  ChargifyBillingEntry,
  InfuraPlus,
  Invoice,
  Offerings,
  Product,
  StripeInvoice,
  Subscription,
  TierOffering,
  TierOfferings,
  User
} from '@infura/packages.api'
import {
  CreditCard,
  InfuraChargifyCoreProductHandles,
  InfuraChargifyFreeProductHandles,
  SubscriptionState
} from '@infura/packages.chargify'
import { Addon } from '@infura/packages.common/servicePlan'
import { getUserChargifySubscriptionReference } from '@infura/packages.common/user'
import { ServiceTier } from '@infura/packages.uks'
import { User as DBUser, ETHTier } from '@infura/packages.user-db'

import { TierHierarchy } from 'lib/offerings'

export const getChargifyBilling = (user: User | DBUser, product: Product): ChargifyBillingEntry | null => {
  const activeSubscription: ChargifyBillingEntry = Object.values(
    get(
      user,
      ['service_plan', 'infura_plus', 'billing'],
      {} as InfuraPlus['billing']
    ) as unknown as ChargifyBillingEntry
  ).find((billingEntry: ChargifyBillingEntry) => {
    return (
      billingEntry.active === true &&
      !!billingEntry.chargify_subscription_id &&
      billingEntry.chargify_subscription_reference === getUserChargifySubscriptionReference(user, product)
    )
  }) as ChargifyBillingEntry

  return activeSubscription || null
}

export const getProductHandleByTier = (
  tier: ServiceTier
): InfuraChargifyFreeProductHandles | InfuraChargifyCoreProductHandles => {
  switch (tier) {
    case ServiceTier.DEVELOPER:
      return InfuraChargifyCoreProductHandles.DEVELOPER
    case ServiceTier.TEAM:
      return InfuraChargifyCoreProductHandles.TEAM
    case ServiceTier.GROWTH:
      return InfuraChargifyCoreProductHandles.GROWTH
    case ServiceTier.ENTERPRISE:
      return InfuraChargifyCoreProductHandles.CUSTOM
    default:
      return InfuraChargifyFreeProductHandles.CORE_FREE
  }
}

export const getTierByProductHandle = (
  productHandle: InfuraChargifyFreeProductHandles | InfuraChargifyCoreProductHandles
): ServiceTier => {
  switch (productHandle) {
    case InfuraChargifyCoreProductHandles.DEVELOPER:
      return ServiceTier.DEVELOPER
    case InfuraChargifyCoreProductHandles.TEAM:
      return ServiceTier.TEAM
    case InfuraChargifyCoreProductHandles.GROWTH:
      return ServiceTier.GROWTH
    case InfuraChargifyCoreProductHandles.CUSTOM:
      return ServiceTier.ENTERPRISE
    default:
      return ServiceTier.CORE
  }
}

export const getTierByETHTier = (tier: ETHTier): ServiceTier => {
  switch (tier) {
    case ETHTier.CUSTOM:
      return ServiceTier.ENTERPRISE
    case ETHTier.TIER_1:
      return ServiceTier.DEVELOPER
    case ETHTier.TIER_2:
      return ServiceTier.TEAM
    case ETHTier.TIER_3:
      return ServiceTier.GROWTH
    case ETHTier.BASE:
      return ServiceTier.CORE
  }
}

export const getIsDowngrading = (currentTier: ServiceTier, tier: ServiceTier) => {
  return TierHierarchy[tier] < TierHierarchy[currentTier]
}

export const isChargifySubDowngradePending = (subscription: Subscription, enforceFree?: boolean) => {
  if (!subscription || !subscription?.next_product_handle) return false

  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
  const currentTier = getTierByProductHandle(subscription.product.handle)
  const nextTier = getTierByProductHandle(
    subscription?.next_product_handle as InfuraChargifyFreeProductHandles | InfuraChargifyCoreProductHandles
  )

  if (enforceFree && nextTier !== ServiceTier.CORE) return false

  return getIsDowngrading(currentTier, nextTier)
}

export const getSlicedCardNumber = (chargifyCard?: CreditCard): string => {
  return chargifyCard?.masked_card_number?.slice(-4) ?? ''
}

export const getCardHolderName = (chargifyCard?: CreditCard): string | null => {
  const firstName = chargifyCard?.first_name || ''
  const lastName = chargifyCard?.last_name || ''
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || null
}

export const getCardExpiration = (chargifyCard?: CreditCard): string => {
  return `${chargifyCard?.expiration_month}/${chargifyCard?.expiration_year?.toString().slice(-2)}`
}

export const getCardBrand = (chargifyCard?: CreditCard): string => {
  const cardNetworkFromResponse = chargifyCard?.card_type

  switch (cardNetworkFromResponse) {
    case 'visa':
      return 'Visa'
    case 'master':
      return 'Mastercard'
    case 'american_express':
      return 'American Express'
    case 'discover':
      return 'Discover'
    case 'jcb':
      return 'JCB'
    default:
      return 'Card'
  }
}

export const getProductNameFromBillingResponse = ({
  productId,
  tiers
}: {
  productId?: string
  tiers: TierOfferings
}) => {
  if (!productId) return ''
  if (productId === 'ethereum_request_addon_v1') return 'Request Packs'

  const productName = productId.slice(0, -3)
  const tier = getTierByETHTier(productName as ETHTier) // stripe used ethTier
  const tierName = tiers[tier]?.name ?? ''
  return tierName ? `${tierName} Plan` : ''
}

type Provider = 'stripe' | 'chargify'

export type UIInvoice = Partial<Omit<Invoice, 'status'> & StripeInvoice & { amount_paid: number; status: string }>

export enum UIInvoicePaymentStatus {
  Paid = 'Paid',
  Unpaid = 'Unpaid',
  Processing = 'Payment Processing',
  Voided = 'Voided'
}

/**
 * Gets the Product type name to display in the invoices table.
 * Chargify and Stripe provide a different response shape
 * @param invoice
 */
export const getProductType = ({ invoice, tiers }: { invoice: UIInvoice; tiers: TierOfferings }) => {
  const provider: Provider = invoice.hasOwnProperty('id') ? 'stripe' : 'chargify'

  if (provider === 'stripe')
    return getProductNameFromBillingResponse({
      productId: get(last(get(invoice, 'lines.data')), 'plan.nickname'),
      tiers
    })

  return get(invoice, 'role') === 'proration'
    ? 'Capacity Packs (Request or Credit)'
    : get(invoice, 'product_name') ||
        getProductNameFromBillingResponse({
          productId: get(invoice, 'lines.data[0].plan.nickname', ''),
          tiers
        })
}

type CalculateTotalParams = {
  tier: ServiceTier
  addons: Array<Addon>
  offerings: Offerings
}

/*
 * Calculates the order subtotal:
 *  Base fare + ( number of request packs * qty of request packs )
 *  Does not include discounts
 */
export const calculateTotal = ({ addons, offerings, tier }: CalculateTotalParams) =>
  (offerings.tiers?.[tier]?.price ?? 0) +
  addons.filter(addon => addon.selected).reduce((f, c) => f + offerings.addons[c.type]?.price * c.quantity, 0)

export const isSubscriptionInValid = (sub: Subscription) => {
  return (
    sub?.state === SubscriptionState.EXPIRED ||
    sub?.state === SubscriptionState.CANCELED ||
    sub?.state === SubscriptionState.FAILED_TO_CREATE ||
    sub?.state === SubscriptionState.SOFT_FAILURE
  )
}

export const sortTierOfferingsByHierarchy = (tiers: TierOfferings): Array<TierOffering> => {
  return Object.values(tiers).sort((a: TierOffering, b: TierOffering) => a.hierarchy - b.hierarchy)
}

export const dollarStringToCents = (amount: string): number => {
  const res = Math.round(parseFloat(amount) * 100)
  return res
}
